import React, { useEffect, useMemo, useState } from 'react';
import * as api from '../api';
import TranscriptDebts from "../components/TranscriptDebts";
import ConvaiAudioPlayer from "../components/AudioPlayerDebts";
import { getCallDebtEntries, getDebtsArray } from '../utils/callDebts';

const CARD = "rounded-2xl border border-[#1f2638] bg-[#0e1320]/50 p-5";
const LBL = "text-xs text-slate-400";
const VAL = "mt-1 text-sm text-slate-200 break-words";

const fmtMoney = (n) => {
  const v = Number(n);
  if (!isFinite(v)) return '—';
  return new Intl.NumberFormat('bg-BG', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);
};

const fmtDateISO = (iso) => {
  const raw = String(iso || '').trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return '—';
  const [y, m, d] = raw.split('-');
  return `${d}.${m}.${y}`;
};

function normBool(v) {
  if (v == null) return null;
  const s = String(v).trim().toLowerCase();
  if (!s) return null;

  if (["yes", "y", "true", "1", "да", "d", "ok"].includes(s)) return true;
  if (["no", "n", "false", "0", "не"].includes(s)) return false;

  return null;
}

function bgYesNo(v) {
  const b = normBool(v);
  if (b === true) return "Да";
  if (b === false) return "Не";
  return "—";
}

function Spinner({ label = "Зареждане…" }) {
  return (
    <div className="inline-flex items-center gap-2 text-sm text-slate-300">
      <span
        className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-current border-t-transparent opacity-80"
        aria-hidden="true"
      />
      <span>{label}</span>
    </div>
  );
}

function toNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function pickTurnStartTs(turn) {
  const start = toNum(turn?.ts_start);
  if (start != null) return start;
  return toNum(turn?.ts);
}

function pickTurnText(turn) {
  const candidates = [
    turn?.text,
    turn?.message,
    turn?.original_message,
    turn?.multivoice_message,
  ];

  for (const v of candidates) {
    const s = String(v ?? '').trim();
    if (s) return s;
  }
  return '';
}

function normalizeTurnRole(role) {
  const s = String(role || '').trim().toLowerCase();
  if (!s) return '';
  if (s === 'assistant' || s === 'agent' || s === 'bot') return 'agent';
  if (s === 'user' || s === 'human' || s === 'customer') return 'user';
  return '';
}

function mapTurnsToTranscript(turns, startedAtRaw) {
  const arr = Array.isArray(turns) ? turns : [];
  const startedAt = toNum(startedAtRaw);

  let firstTs = null;
  for (const t of arr) {
    const ts = pickTurnStartTs(t);
    if (ts != null) {
      firstTs = ts;
      break;
    }
  }

  const normalized = arr
    .map((turn) => {
      const role = normalizeTurnRole(turn?.role);
      const text = pickTurnText(turn);
      if (!role || !text) return null;

      const ts = pickTurnStartTs(turn);
      let start = null;
      if (ts != null && startedAt != null) {
        start = Math.max(0, ts - startedAt);
      } else if (ts != null && firstTs != null) {
        start = Math.max(0, ts - firstTs);
      }

      return {
        role,
        message: text,
        original_message: text,
        time_in_call_secs: start,
      };
    })
    .filter(Boolean);

  return normalized.reduce((acc, cur) => {
    const prev = acc[acc.length - 1];
    if (!prev || prev.role !== cur.role) {
      acc.push(cur);
      return acc;
    }

    const prevText = String(prev.message || prev.original_message || '').trim();
    const curText = String(cur.message || cur.original_message || '').trim();
    const mergedText = [prevText, curText].filter(Boolean).join(' ');

    prev.message = mergedText;
    prev.original_message = mergedText;

    if (typeof prev.time_in_call_secs !== 'number' && typeof cur.time_in_call_secs === 'number') {
      prev.time_in_call_secs = cur.time_in_call_secs;
    }

    return acc;
  }, []);
}
export default function ConvaiHistoryDetails({ conversationId, onBack }) {
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [data, setData] = useState(null);

  const [tLoading, setTLoading] = useState(false);
  const [tErr, setTErr] = useState('');
  const [convai, setConvai] = useState(null);

  const [sLoading, setSLoading] = useState(false);
  const [sErr, setSErr] = useState('');
  const [scenarios, setScenarios] = useState([]);

  const callId = String(conversationId || '').trim();

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setSLoading(true);
      setSErr('');
      try {
        const arr = await api.listScenarios();
        if (cancelled) return;
        setScenarios(Array.isArray(arr) ? arr : []);
      } catch (e) {
        if (cancelled) return;
        setSErr(e?.message || 'Грешка при зареждане на сценарии.');
        setScenarios([]);
      } finally {
        if (!cancelled) setSLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!callId) {
        setErr('Липсва call_id.');
        setData(null);
        setConvai(null);
        setTErr('');
        setLoading(false);
        setTLoading(false);
        return;
      }

      setLoading(true);
      setErr('');
      setTLoading(true);
      setTErr('');

      try {
        const res = await api.getCallById(callId, { include_turns: 1 });
        if (cancelled) return;

        const call = (res?.call && typeof res.call === 'object') ? res.call : null;
        const state = String(call?.state || '').trim().toLowerCase();

        if (!call || state === 'unknown') {
          setData(null);
          setConvai(null);
          setTErr('');
          return;
        }

        const transcript = mapTurnsToTranscript(call?.turns, call?.started_at);

        setData(call);
        setConvai({ transcript });
      } catch (e) {
        if (cancelled) return;
        setErr(e?.message || 'Грешка при зареждане.');
        setData(null);
        setConvai(null);
        setTErr('');
      } finally {
        if (!cancelled) {
          setLoading(false);
          setTLoading(false);
        }
      }
    })();

    return () => { cancelled = true; };
  }, [callId]);

  const snapshot = (data?.client_snapshot && typeof data.client_snapshot === 'object')
    ? data.client_snapshot
    : {};

  const p = (snapshot?.personal && typeof snapshot.personal === 'object')
    ? snapshot.personal
    : ((data?.personal && typeof data.personal === 'object') ? data.personal : {});

  const c = (snapshot?.contacts && typeof snapshot.contacts === 'object')
    ? snapshot.contacts
    : ((data?.contacts && typeof data.contacts === 'object') ? data.contacts : {});

  const debtSource =
    (data?.debt && typeof data.debt === 'object')
      ? { ...snapshot, debt: data.debt }
      : snapshot;
  const debts = getDebtsArray(debtSource);
  const callDebtEntries = getCallDebtEntries(debtSource, debts);
  const displayDebts = callDebtEntries.length
    ? callDebtEntries.map((entry) => entry.debt)
    : debts.slice(0, 1);

  const thirdPersonLabel = useMemo(() => {
    const base = bgYesNo(c.thirdPerson);
    const hasThirdDetails = [
      c.thirdPersonPhone,
      c.thirdPersonFirstName,
      c.thirdPersonLastName,
    ].some((x) => String(x || "").trim());

    if (base === "Да" && !hasThirdDetails) {
      return "Да (липсват данни)";
    }
    return base;
  }, [c.thirdPerson, c.thirdPersonPhone, c.thirdPersonFirstName, c.thirdPersonLastName]);

  const title = useMemo(() => {
    const name = `${p?.firstName || ''} ${p?.lastName || ''}`.trim();
    return name || 'Conversation';
  }, [p?.firstName, p?.lastName]);

  const scenarioName = useMemo(() => {
    const sid = String(snapshot?.scenario || data?.scenario || '').trim();
    if (!sid) return '—';

    const hit = (scenarios || []).find((x) => String(x?.id || x?._id || '').trim() === sid);
    const name = String(hit?.name || hit?.title || '').trim();
    return name || sid;
  }, [snapshot?.scenario, data?.scenario, scenarios]);

  const showPageLoading = loading && !data && !err;

  return (
    <div className="card rounded-2xl p-6 relative">
      {showPageLoading && (
        <div className="absolute inset-0 z-10 rounded-2xl bg-[#0b1020]/60 backdrop-blur-[2px] flex items-center justify-center">
          <div className="rounded-2xl border border-[#1f2638] bg-[#0e1320]/80 px-5 py-4 shadow-lg">
            <Spinner label="Зареждане на детайли…" />
          </div>
        </div>
      )}

      <div className="flex items-center gap-3 flex-wrap">
        <button className="btn btn-ghost" onClick={onBack}>Назад</button>
        <h2 className="text-lg font-bold whitespace-nowrap">{title}</h2>
      </div>

      {err && (
        <div className="mt-4 rounded-xl border border-rose-600/30 bg-rose-600/10 px-3 py-2 text-sm text-rose-200">
          {err}
        </div>
      )}

      {!err && !loading && !data && (
        <div className="mt-6 text-sm text-slate-400">Няма данни.</div>
      )}

      {data && (
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-[minmax(360px,1.15fr),minmax(360px,1fr)] gap-4">
          <section className={`${CARD} lg:sticky lg:top-6 self-start`}>
            <div className="text-sm font-semibold tracking-wider text-slate-200">ТРАНСКРИПТ</div>

            {tErr && (
              <div className="mt-4 rounded-xl border border-rose-600/30 bg-rose-600/10 px-3 py-2 text-sm text-rose-200">
                {tErr}
              </div>
            )}

            {!tErr && tLoading && (
              <div className="mt-4">
                <Spinner label="Зареждане на транскрипт…" />
              </div>
            )}

            {!tErr && !tLoading && (!convai || !Array.isArray(convai?.transcript) || convai.transcript.length === 0) && (
              <div className="mt-4 text-sm text-slate-400">Няма транскрипт.</div>
            )}

            {!tErr && !tLoading && convai && (
              <div className="mt-4 max-h-[70vh] overflow-auto nice-scrollbar pr-1">
                <TranscriptDebts convai={convai} />
              </div>
            )}
          </section>

          <div className="grid grid-cols-1 gap-4">
            <section className={CARD}>
              <div className="text-sm font-semibold tracking-wider text-slate-200">АУДИО</div>
              <div className="mt-4">
                <ConvaiAudioPlayer
                  conversationId={callId}
                  clientName={`${p?.firstName || ""} ${p?.lastName || ""}`.trim()}
                  clientEgn={p?.egn || ""}
                />
              </div>
            </section>

            <section className={CARD}>
              <div className="text-sm font-semibold tracking-wider text-slate-200">ЛИЧНИ ДАННИ</div>
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <div className={LBL}>Име</div>
                  <div className={VAL}>{p.firstName || '—'}</div>
                </div>
                <div>
                  <div className={LBL}>Фамилия</div>
                  <div className={VAL}>{p.lastName || '—'}</div>
                </div>
                <div className="sm:col-span-2">
                  <div className={LBL}>ЕГН</div>
                  <div className={VAL}>{p.egn || '—'}</div>
                </div>
              </div>
            </section>

            <section className={CARD}>
              <div className="text-sm font-semibold tracking-wider text-slate-200">КОНТАКТИ</div>
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <div className={LBL}>Телефон</div>
                  <div className={VAL}>{c.phone || '—'}</div>
                </div>

                <div>
                  <div className={LBL}>Трето лице</div>
                  <div className={VAL}>{thirdPersonLabel}</div>
                </div>

                {normBool(c.thirdPerson) === true && (
                  <>
                    <div>
                      <div className={LBL}>Телефон (трето лице)</div>
                      <div className={VAL}>{c.thirdPersonPhone || '—'}</div>
                    </div>
                    <div>
                      <div className={LBL}>Име (трето лице)</div>
                      <div className={VAL}>{c.thirdPersonFirstName || '—'}</div>
                    </div>
                    <div>
                      <div className={LBL}>Фамилия (трето лице)</div>
                      <div className={VAL}>{c.thirdPersonLastName || '—'}</div>
                    </div>
                  </>
                )}
              </div>
            </section>

            <section className={CARD}>
              <div className="text-sm font-semibold tracking-wider text-slate-200">ЗАДЪЛЖЕНИЯ</div>
              {!displayDebts.length ? (
                <div className="mt-4 text-sm text-slate-400">Няма задължения.</div>
              ) : (
                <div className="mt-4 space-y-3">
                  {displayDebts.map((debt, index) => (
                    <div
                      key={`call-debt-${index}`}
                      className="rounded-xl border border-[#1f2638] bg-[#0f1523] p-4"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="text-sm font-semibold text-slate-100">{debt?.creditor || '—'}</div>
                        <span className="rounded border border-emerald-500/30 bg-emerald-600/15 px-1.5 py-0.5 text-[10px] text-emerald-200 whitespace-nowrap">
                          за обаждане
                        </span>
                      </div>
                      <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div>
                          <div className={LBL}>Сума</div>
                          <div className={VAL}>{fmtMoney(debt?.amount)}</div>
                        </div>
                        <div>
                          <div className={LBL}>Вноска</div>
                          <div className={VAL}>{debt?.installment != null && debt?.installment !== '' ? fmtMoney(debt.installment) : '—'}</div>
                        </div>
                        <div>
                          <div className={LBL}>Падеж</div>
                          <div className={VAL}>{fmtDateISO(debt?.dueDate ?? debt?.due_date)}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section className={CARD}>
              <div className="text-sm font-semibold tracking-wider text-slate-200">Сценарий</div>
              <div>
                <div className={`${VAL} ${scenarioName === (snapshot?.scenario || data?.scenario || '') ? 'font-mono' : ''}`}>
                  {scenarioName}

                  {!!sErr && (
                    <span className="ml-2 text-xs text-rose-300" title={sErr}>
                      (scenarios error)
                    </span>
                  )}

                  {sLoading && !sErr && (
                    <span className="ml-2 inline-flex items-center gap-2 text-xs text-slate-500">
                      <span className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent opacity-80" />
                      зареждане…
                    </span>
                  )}
                </div>
              </div>
            </section>
          </div>
        </div>
      )}
    </div>
  );
}
